import { z } from 'zod';

// Validación de archivos subidos (magic bytes + MIME + size) — Persona Moral F4.
export * from './validate-upload';

// Nombre visible del usuario (saludos del portal).
export * from './display-name';

// ============================================================================
// MASKS - Para ocultar información sensible (PII)
// ============================================================================

/**
 * Oculta una CLABE mostrando solo los últimos 4 dígitos
 * @example maskCLABE("012345678901234567") => "****4567"
 */
export function maskCLABE(clabe: string): string {
  if (!clabe || clabe.length < 4) return clabe;
  return `****${clabe.slice(-4)}`;
}

/**
 * Oculta un email mostrando solo el primer carácter y el dominio
 * @example maskEmail("jose.garcia@example.com") => "j***@example.com"
 */
export function maskEmail(email: string): string {
  if (!email) return email;
  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) return email;
  return `${localPart.charAt(0)}***@${domain}`;
}

/**
 * Oculta un número de teléfono mostrando solo los últimos 4 dígitos
 * @example maskPhone("5512345678") => "****5678"
 */
export function maskPhone(phone: string): string {
  if (!phone || phone.length < 4) return phone;
  const digits = phone.replace(/\D/g, '');
  return `****${digits.slice(-4)}`;
}

/**
 * Oculta una CURP mostrando solo los primeros 4 y últimos 2 caracteres
 * @example maskCURP("GARC850101HDFRRL09") => "GARC**********09"
 */
export function maskCURP(curp: string): string {
  if (!curp || curp.length < 6) return curp;
  return `${curp.slice(0, 4)}${'*'.repeat(curp.length - 6)}${curp.slice(-2)}`;
}

/**
 * Oculta un RFC mostrando solo los primeros 4 caracteres
 * @example maskRFC("GARC850101AB9") => "GARC*********"
 */
export function maskRFC(rfc: string): string {
  if (!rfc || rfc.length < 4) return rfc;
  return `${rfc.slice(0, 4)}${'*'.repeat(rfc.length - 4)}`;
}

/**
 * Oculta un número de tarjeta mostrando solo los últimos 4 dígitos
 * @example maskCardNumber("4111111111111111") => "•••• •••• •••• 1111"
 */
export function maskCardNumber(cardNumber: string): string {
  if (!cardNumber) return cardNumber;
  const digits = cardNumber.replace(/\D/g, '');
  if (digits.length < 4) return cardNumber;
  return `•••• •••• •••• ${digits.slice(-4)}`;
}

// ============================================================================
// VALIDATORS - Para validar formatos mexicanos
// ============================================================================

/**
 * Valida que una CLABE tenga 18 dígitos
 * Nota: No valida el dígito verificador
 */
export function isValidCLABE(clabe: string): boolean {
  if (!clabe) return false;
  const digits = clabe.replace(/\D/g, '');
  return /^\d{18}$/.test(digits);
}

/**
 * Valida el dígito verificador de una CLABE
 */
export function validateCLABEChecksum(clabe: string): boolean {
  if (!isValidCLABE(clabe)) return false;

  const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7];
  const digits = clabe.split('').map(Number);

  let sum = 0;
  for (let i = 0; i < 17; i++) {
    const digit = digits[i] ?? 0;
    const weight = weights[i] ?? 1;
    sum += (digit * weight) % 10;
  }

  const checksum = (10 - (sum % 10)) % 10;
  return checksum === digits[17];
}

/**
 * Regex canónica de CURP. RFC-aligned:
 *   - pos 1:    cualquier letra mayúscula
 *   - pos 2:    vocal o X (RENAPO usa X cuando no hay vocal — apellido extranjero,
 *               un solo apellido, etc.)
 *   - pos 3-4:  letras
 *   - pos 5-10: AAMMDD
 *   - pos 11:   H/M (sexo)
 *   - pos 12-16: 5 letras (entidad + consonantes internas)
 *   - pos 17:   letra o dígito (homoclave generacional)
 *   - pos 18:   dígito verificador
 *
 * Es la única fuente de verdad: importar `CURP_REGEX` o usar `isValidCURP()`
 * en vez de re-declararla. Mantenerla sincronizada con
 * `packages/core/src/schemas/onboarding.schema.ts > curpStepSchema`.
 */
export const CURP_REGEX = /^[A-Z][AEIOUX][A-Z]{2}\d{6}[HM][A-Z]{5}[A-Z0-9]\d$/;

/**
 * Regex canónica del RFC de PERSONA MORAL (empresa): 12 caracteres —
 *   - pos 1-3:   3 letras (raíz; A-Z, Ñ o &)
 *   - pos 4-9:   AAMMDD (fecha de constitución)
 *   - pos 10-12: homoclave (letra o dígito)
 *
 * A diferencia del RFC de persona física (13 chars, 4 letras). Es la única
 * fuente de verdad: importar `RFC_MORAL_REGEX` en vez de re-declararla.
 * Mantener sincronizada con
 * `packages/core/src/schemas/company.schema.ts > rfcMoralSchema`.
 */
export const RFC_MORAL_REGEX = /^[A-ZÑ&]{3}\d{6}[A-Z0-9]{3}$/;

/**
 * Valida el formato de una CURP. Ver `CURP_REGEX` para el detalle del patrón.
 */
export function isValidCURP(curp: string): boolean {
  if (!curp) return false;
  return CURP_REGEX.test(curp.toUpperCase());
}

// Tabla de pesos RENAPO: cada carácter del CURP tiene un valor numérico
// asignado para calcular el dígito verificador. Fuente:
// https://es.wikipedia.org/wiki/Clave_%C3%9Anica_de_Registro_de_Poblaci%C3%B3n
// Fórmula oficial: sum(value(c_i) * (18 - i)) for i=0..16, mod 10,
// (10 - mod) % 10 = dígito verificador (pos 17 base 0 → pos 18 base 1).
const CURP_CHECKSUM_VALUES: Record<string, number> = {
  '0': 0,
  '1': 1,
  '2': 2,
  '3': 3,
  '4': 4,
  '5': 5,
  '6': 6,
  '7': 7,
  '8': 8,
  '9': 9,
  A: 10,
  B: 11,
  C: 12,
  D: 13,
  E: 14,
  F: 15,
  G: 16,
  H: 17,
  I: 18,
  J: 19,
  K: 20,
  L: 21,
  M: 22,
  N: 23,
  Ñ: 24,
  O: 25,
  P: 26,
  Q: 27,
  R: 28,
  S: 29,
  T: 30,
  U: 31,
  V: 32,
  W: 33,
  X: 34,
  Y: 35,
  Z: 36,
};

/**
 * Valida el dígito verificador (pos 18) del CURP con la fórmula oficial de
 * RENAPO. Esto rechaza CURPs sintácticamente correctos pero con dígito
 * verificador incorrecto — son la mayoría de los CURPs falsos típicos.
 *
 * Requisito CNBV/Ley Fintech Art. 58 para KYC: identificación validable
 * contra registro oficial. Validar el checksum client-side reduce carga al
 * RENAPO y rechaza inputs malformados antes de gastar la llamada.
 *
 * Retorna false si el formato base falla, evitando excepciones.
 */
export function isValidCURPChecksum(curp: string): boolean {
  if (!isValidCURP(curp)) return false;
  const upper = curp.toUpperCase();

  let sum = 0;
  for (let i = 0; i < 17; i++) {
    const value = CURP_CHECKSUM_VALUES[upper[i]!];
    if (value === undefined) return false;
    sum += value * (18 - i);
  }
  const computed = (10 - (sum % 10)) % 10;
  const expected = Number.parseInt(upper[17]!, 10);
  return Number.isFinite(expected) && computed === expected;
}

/**
 * Datos codificados dentro del CURP (pos 5-10 = fecha YYMMDD, pos 11 = sexo).
 * Devuelve null si el formato no es válido.
 */
export interface CurpDecoded {
  /** Fecha de nacimiento en formato ISO YYYY-MM-DD. */
  birthDateIso: string;
  /** Sexo codificado: 'H' = hombre/masculino, 'M' = mujer/femenino. */
  sex: 'H' | 'M';
  /** Código de entidad federativa (pos 12-13). */
  stateCode: string;
}

/**
 * Decodifica los campos auto-contenidos del CURP (fecha de nacimiento, sexo,
 * estado). Útil para validar coherencia client-side ANTES de enviar al BE.
 *
 * Heurística para el año: el CURP codifica solo 2 dígitos. Si pos 17 es letra
 * (homoclave generacional A-Z) → 2000+; si es dígito → 1900+. Es la regla
 * oficial de RENAPO (las personas nacidas a partir del año 2000 tienen
 * homoclave alfabética).
 */
export function decodeCurp(curp: string): CurpDecoded | null {
  if (!isValidCURP(curp)) return null;
  const upper = curp.toUpperCase();

  const yy = upper.slice(4, 6);
  const mm = upper.slice(6, 8);
  const dd = upper.slice(8, 10);
  const sex = upper[10] as 'H' | 'M';
  const stateCode = upper.slice(11, 13);
  const homoclave = upper[16]!;

  const century = /[A-Z]/.test(homoclave) ? 2000 : 1900;
  const year = century + Number.parseInt(yy, 10);

  // Validar fecha real (rechaza 31 de febrero, etc.)
  const monthIdx = Number.parseInt(mm, 10) - 1;
  const dayNum = Number.parseInt(dd, 10);
  const date = new Date(Date.UTC(year, monthIdx, dayNum));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== monthIdx ||
    date.getUTCDate() !== dayNum
  ) {
    return null;
  }

  return {
    birthDateIso: `${year}-${mm}-${dd}`,
    sex,
    stateCode,
  };
}

/**
 * Regex de nombres de persona. Acepta:
 *   - letras Unicode (incluye á/é/í/ó/ú/ü/ñ y cualquier diacrítico),
 *   - espacios (para nombres compuestos: "María José"),
 *   - guiones y apóstrofes (apellidos como "Gómez-López" u "O'Brien").
 *
 * Rechaza dígitos y caracteres especiales (@, #, $, etc.).
 * Mantenerla sincronizada con el schema `dataStepSchema` y los usos en
 * la página de validación de datos personales.
 */
export const NAME_REGEX = /^[\p{L}\s'-]+$/u;

/**
 * Regex de código postal mexicano: exactamente 5 dígitos.
 * Mantenerla sincronizada con `isValidPostalCode()` (que ya valida el formato
 * para llamadas no-form). Aquí se exporta como constante para usarla en
 * schemas zod sin duplicar la expresión.
 */
export const POSTAL_CODE_REGEX = /^\d{5}$/;

/**
 * Regex de texto de dirección (ciudad, municipio/alcaldía, colonia, calle).
 * Acepta:
 *   - letras Unicode (incluye diacríticos),
 *   - dígitos (para "Avenida 5 de Mayo", "16 de Septiembre"),
 *   - espacios,
 *   - punto (para abreviaciones: "Av.", "Col."),
 *   - guiones y apóstrofes.
 *
 * Rechaza caracteres especiales (@, #, $, %, !, &, *, etc.).
 */
export const ADDRESS_TEXT_REGEX = /^[\p{L}\p{N}\s.'-]+$/u;

/**
 * Sanitiza la entrada del campo CURP en tiempo real:
 *   - fuerza mayúsculas,
 *   - elimina cualquier carácter que no sea letra mayúscula o dígito
 *     (espacios, acentos, símbolos),
 *   - trunca a 18 caracteres (la longitud canónica del CURP).
 *
 * Pensado para usarse en handlers `onChange` de inputs controlados. Para
 * validar el formato completo, usar `CURP_REGEX` o `isValidCURP()`.
 */
export function sanitizeCurpInput(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 18);
}

/**
 * Opción de un catálogo (par value/label) — value es el código que se
 * persiste, label el texto que se muestra al usuario.
 */
export interface CatalogOption {
  value: string;
  label: string;
}

/**
 * Resuelve el label de un catálogo a partir del código guardado. Útil para
 * mostrar el texto humano (ej. "Soltero(a)") en vistas de read-only que solo
 * conocen el code (ej. "single_a"). Devuelve cadena vacía cuando no hay code
 * o no hay coincidencia, para que el render no muestre "undefined".
 */
export function labelForCode(
  options: readonly CatalogOption[],
  code: string | undefined | null
): string {
  if (!code) return '';
  return options.find(o => o.value === code)?.label ?? '';
}

/**
 * Valida el formato de un RFC
 * Persona física: 4 letras + 6 dígitos + 3 homoclave (13 caracteres)
 * Persona moral: 3 letras + 6 dígitos + 3 homoclave (12 caracteres)
 */
export function isValidRFC(rfc: string): boolean {
  if (!rfc) return false;
  // Persona moral (12 chars) o física (13 chars)
  const pattern = /^[A-ZÑ&]{3,4}\d{6}[A-Z\d]{3}$/;
  return pattern.test(rfc.toUpperCase());
}

/**
 * Valida un número de teléfono mexicano (10 dígitos)
 */
export function isValidMexicanPhone(phone: string): boolean {
  if (!phone) return false;
  const digits = phone.replace(/\D/g, '');
  // 10 dígitos o con código de país (52 o +52)
  return /^(52)?\d{10}$/.test(digits) || /^\d{10}$/.test(digits);
}

/**
 * Valida un código postal mexicano (5 dígitos)
 */
export function isValidPostalCode(postalCode: string): boolean {
  if (!postalCode) return false;
  return /^\d{5}$/.test(postalCode);
}

/**
 * Largo del número de serie del certificado de la e.firma (FIEL) del SAT.
 * Se exporta porque los inputs lo necesitan para su `maxLength` y para recortar
 * el pegado: la constante y la validación tienen que salir del mismo sitio o se
 * separan (el campo del propietario real no tenía ni una ni la otra).
 */
export const FIEL_SERIAL_LENGTH = 20;

/**
 * Valida el número de serie del certificado de la e.firma (FIEL): 20 dígitos,
 * sin letras ni separadores — p. ej. `00001000000512345678`.
 *
 * Cadena vacía es `false`: quien tenga el campo como opcional debe decidir
 * ANTES si hay algo que validar, igual que `isValidRFC` o `isValidPostalCode`.
 */
export function isValidFielSerial(serial: string): boolean {
  if (!serial) return false;
  return new RegExp(`^\\d{${FIEL_SERIAL_LENGTH}}$`).test(serial.trim());
}

/**
 * Valida un email. Delega en la misma regla de Zod que usan los schemas del
 * onboarding (`emailSchema`, `beneficial-owner-identity`, `legal-rep`,
 * `account-user`) para que exista UNA sola definición de "correo válido" en
 * todo el repo — antes, además de ésta, había tres copias del mismo regex
 * laxo repetidas en el flujo de registro (`.../auth/lib/registerStepFlow.ts`,
 * `.../register-user/hooks/useRegisterEmailStep.ts`), que aceptaban entradas
 * como `juan@dominio.c` o `juan@tankef..com` que el resto del producto ya
 * rechaza. Endurecer aquí endurece a los tres a la vez.
 */
const EMAIL_SCHEMA = z.string().trim().email();

export function isValidEmail(email: string): boolean {
  if (!email) return false;
  return EMAIL_SCHEMA.safeParse(email).success;
}

// ============================================================================
// FORMATTERS - Para dar formato a datos
// ============================================================================

export interface ParsedBirthDate {
  dd: string;
  mm: string;
  yyyy: string;
}

const BIRTH_DATE_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/**
 * Parses a Spanish-format birth date (`DD/MM/AAAA`) into its parts.
 * Returns `null` when the input is missing or doesn't match the expected format.
 *
 * Use the parts to build other formats (e.g. ISO `YYYY-MM-DD` for backend
 * payloads). Lets the caller distinguish "missing" from "malformed" by
 * checking the original value before/after calling.
 */
export function parseBirthDate(value: string | undefined | null): ParsedBirthDate | null {
  const match = value?.match(BIRTH_DATE_REGEX);
  // The regex has exactly 3 capture groups; when match succeeds they are
  // guaranteed strings, but TS's `noUncheckedIndexedAccess` can't prove it.
  if (!match || !match[1] || !match[2] || !match[3]) return null;
  return { dd: match[1], mm: match[2], yyyy: match[3] };
}

/**
 * Returns true when `dd/mm/yyyy` is a real calendar date (rejects e.g.
 * `31/02/2020`, which `parseBirthDate`'s regex alone would let through).
 */
function isRealCalendarDate(dd: number, mm: number, yyyy: number): boolean {
  const date = new Date(yyyy, mm - 1, dd);
  return date.getFullYear() === yyyy && date.getMonth() === mm - 1 && date.getDate() === dd;
}

/**
 * Convierte `DD/MM/AAAA` (formato de despliegue de `DateInput`) a ISO
 * `YYYY-MM-DD` (formato que espera el BE / `<input type="date">`). Devuelve
 * `null` si el valor está incompleto o no es una fecha calendario real —
 * así el caller nunca manda una fecha a medio escribir.
 *
 * @example displayToIsoDate('24/12/2025') => '2025-12-24'
 * @example displayToIsoDate('31/02/2020') => null (no existe el 31 de febrero)
 */
export function displayToIsoDate(value: string | undefined | null): string | null {
  const parsed = parseBirthDate(value);
  if (!parsed) return null;
  const { dd, mm, yyyy } = parsed;
  if (!isRealCalendarDate(Number(dd), Number(mm), Number(yyyy))) return null;
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Convierte ISO `YYYY-MM-DD` a `DD/MM/AAAA` (formato de despliegue de
 * `DateInput`). Devuelve cadena vacía si el valor está vacío o mal formado.
 *
 * @example isoToDisplayDate('2025-12-24') => '24/12/2025'
 */
export function isoToDisplayDate(value: string | undefined | null): string {
  if (!value) return '';
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match || !match[1] || !match[2] || !match[3]) return '';
  const [, yyyy, mm, dd] = match;
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Fecha de hoy en ISO `YYYY-MM-DD` (hora local). Útil para comparar contra una
 * fecha de nacimiento y rechazar fechas futuras sin arrastrar zonas horarias:
 * al ser comparación lexicográfica de strings `YYYY-MM-DD`, `a <= b` equivale a
 * "a no es posterior a b".
 *
 * @example displayToIsoDate(x)! <= todayIsoDate()  // x no es futura
 */
export function todayIsoDate(): string {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mm}-${dd}`;
}

/**
 * Fecha de hoy menos `years` años, en ISO `YYYY-MM-DD` (hora local). Sirve para
 * acotar la edad de una fecha de nacimiento sin aritmética de milisegundos:
 * "nació hace al menos N años" ⇔ `birthIso <= isoDateMinusYears(N)`.
 *
 * @example isoDateMinusYears(18) // fecha máxima de nacimiento para ser mayor de edad
 */
export function isoDateMinusYears(years: number): string {
  const now = new Date();
  const d = new Date(now.getFullYear() - years, now.getMonth(), now.getDate());
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * `true` cuando `value` (ISO `YYYY-MM-DD`) es una fecha calendario real —
 * rechaza formatos válidos pero imposibles como `2020-13-40`, que un simple
 * regex `\d{4}-\d{2}-\d{2}` dejaría pasar.
 */
export function isRealIsoDate(value: string | undefined | null): boolean {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match || !match[1] || !match[2] || !match[3]) return false;
  return isRealCalendarDate(Number(match[3]), Number(match[2]), Number(match[1]));
}

/**
 * Formatea una fecha en formato largo
 * @example formatDate(new Date()) => "24 de diciembre de 2025"
 */
export function formatDate(date: Date, locale = 'es-MX'): string {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

/**
 * Formatea una fecha en formato corto
 * @example formatDateShort(new Date()) => "24/12/2025"
 */
export function formatDateShort(date: Date, locale = 'es-MX'): string {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/**
 * Formatea una fecha con hora
 * @example formatDateTime(new Date()) => "24 dic 2025, 14:30"
 */
export function formatDateTime(date: Date, locale = 'es-MX'): string {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

/**
 * Formatea una fecha en formato relativo
 * @example formatRelativeTime(new Date(Date.now() - 3600000)) => "hace 1 hora"
 */
export function formatRelativeTime(date: Date, locale = 'es-MX'): string {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const diff = date.getTime() - Date.now();
  const diffSeconds = Math.round(diff / 1000);
  const diffMinutes = Math.round(diff / 60000);
  const diffHours = Math.round(diff / 3600000);
  const diffDays = Math.round(diff / 86400000);

  if (Math.abs(diffSeconds) < 60) {
    return rtf.format(diffSeconds, 'second');
  } else if (Math.abs(diffMinutes) < 60) {
    return rtf.format(diffMinutes, 'minute');
  } else if (Math.abs(diffHours) < 24) {
    return rtf.format(diffHours, 'hour');
  } else {
    return rtf.format(diffDays, 'day');
  }
}

/**
 * Formatea fecha+hora sin año — para columnas de tabla en mobile donde el espacio es reducido
 * @example formatDateTimeShort(new Date()) => "24 dic, 14:30"
 */
export function formatDateTimeShort(date: Date, locale = 'es-MX'): string {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

/**
 * Formatea una fecha en formato compacto con año de 2 dígitos — para tablas con espacio reducido
 * @example formatDateCompact(new Date()) => "24/12/25"
 */
export function formatDateCompact(date: Date, locale = 'es-MX'): string {
  // Guard: `Intl.DateTimeFormat.format` lanza RangeError ("date value is not finite")
  // ante una fecha inválida → no debe tumbar el render. Devuelve em-dash.
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/**
 * Serializa una fecha en formato ISO (YYYY-MM-DD) para parámetros de API.
 * Siempre en zona local — no convierte a UTC.
 * @example formatDateISO(new Date('2025-12-24')) => "2025-12-24"
 */
export function formatDateISO(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Abreviaturas de mes (EN + ES) → índice 0-based. El backend serializa las fechas
// de amortización como `DD/Mon/AAAA` con mes en inglés (ej. "08/Jun/2026").
const MONTH_ABBR: Record<string, number> = {
  jan: 0,
  ene: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  abr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  ago: 7,
  sep: 8,
  sept: 8,
  oct: 9,
  nov: 10,
  dec: 11,
  dic: 11,
};

/**
 * Parsea una fecha del API a una `Date` **local**, evitando dos footguns:
 *  1. El off-by-one de TZ: `new Date('2026-06-04')` se interpreta en UTC y puede
 *     restar un día en husos negativos (México).
 *  2. Formatos no-ISO frágiles entre navegadores: Safari no parsea `DD/Mon/AAAA`
 *     con `new Date` y devuelve `Invalid Date`, que luego revienta `Intl.format`.
 *
 * Soporta:
 *  - ISO date-only `AAAA-MM-DD` → local, con validación de rango (rechaza mes 13, etc.).
 *  - `DD/Mon/AAAA` con mes abreviado EN/ES (ej. "08/Jun/2026", "15/Dic/2026").
 *  - Datetime ISO completo → delegado a `new Date`.
 *
 * Fuente única de parseo de fechas del backend (reemplaza el antiguo `parseDateOnly`).
 * @returns una `Date` válida, o `null` si el valor es vacío/inválido.
 * @example parseApiDate('2026-06-04')  // 04 jun 2026 local
 * @example parseApiDate('08/Jun/2026') // 08 jun 2026 local
 */
export function parseApiDate(value: string | undefined | null): Date | null {
  if (!value) return null;
  // ISO date-only AAAA-MM-DD → local (evita el shift de TZ; ver docs/fixes/june26/06 §5).
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (iso) {
    const y = Number(iso[1]);
    const m = Number(iso[2]);
    const d = Number(iso[3]);
    const date = new Date(y, m - 1, d);
    // Rechaza valores fuera de rango (p.ej. mes 13) que JS haría rollover.
    const valid = date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
    return valid ? date : null;
  }
  // DD/Mon/AAAA con mes abreviado EN/ES (ej. "08/Jun/2026").
  const named = /^(\d{1,2})\/([A-Za-z]{3,4})\/(\d{4})$/.exec(value);
  if (named) {
    const [, day, mon, year] = named;
    const month = mon ? MONTH_ABBR[mon.toLowerCase()] : undefined;
    if (month === undefined || !day || !year) return null;
    const date = new Date(Number(year), month, Number(day));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Formatea una fecha del API en `locale`, a salvo del off-by-one de TZ.
 * Devuelve `fallback` cuando el valor es vacío o inválido.
 * @example formatApiDate('2026-06-04') => "4/6/2026"
 */
export function formatApiDate(
  value: string | undefined | null,
  locale = 'es-MX',
  fallback = '—'
): string {
  const date = parseApiDate(value);
  return date ? date.toLocaleDateString(locale) : fallback;
}

/**
 * Formatea un countdown en formato MM:SS
 * @example formatCountdown(125000) => "2:05"
 */
export function formatCountdown(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Formatea un número de teléfono mexicano
 * @example formatPhone("5512345678") => "55 1234 5678"
 */
export function formatPhone(phone: string): string {
  if (!phone) return phone;
  const digits = phone.replace(/\D/g, '');

  // Si tiene código de país
  if (digits.length === 12 && digits.startsWith('52')) {
    const local = digits.slice(2);
    return `+52 ${local.slice(0, 2)} ${local.slice(2, 6)} ${local.slice(6)}`;
  }

  // 10 dígitos local
  if (digits.length === 10) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
  }

  return phone;
}

/**
 * Formatea una CLABE con espacios
 * @example formatCLABE("012345678901234567") => "012 345 6789 0123 4567"
 */
export function formatCLABE(clabe: string): string {
  if (!clabe) return clabe;
  const digits = clabe.replace(/\D/g, '');
  if (digits.length !== 18) return clabe;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)} ${digits.slice(10, 14)} ${digits.slice(14)}`;
}

/**
 * Formatea un número con separadores de miles
 * @example formatNumber(1234567) => "1,234,567"
 */
export function formatNumber(value: number, locale = 'es-MX'): string {
  return new Intl.NumberFormat(locale).format(value);
}

/**
 * Parsea el texto de un input de dinero a número decimal (unidades MXN),
 * tolerante a símbolos de formato ($, comas, espacios, sufijo "MXN"). Devuelve
 * 0 si el valor es vacío o no es numérico. Pensado para gating de UI y
 * conversión previa al envío; NO sustituye la validación de montos.
 *
 * Reemplaza el patrón disperso `parseFloat(x.replace(/[$,]/g, '')) || 0`.
 * @example parseMoneyToNumber("$1,234.50 MXN") => 1234.5
 * @example parseMoneyToNumber("") => 0
 */
export function parseMoneyToNumber(value: string | undefined | null): number {
  if (!value) return 0;
  const parsed = parseFloat(value.replace(/[^0-9.]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Formatea un porcentaje
 * @example formatPercent(0.1234) => "12.34%"
 */
export function formatPercent(value: number, decimals = 2, locale = 'es-MX'): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

// ============================================================================
// STRING UTILITIES
// ============================================================================

/**
 * Capitaliza la primera letra de cada palabra
 * @example titleCase("juan carlos") => "Juan Carlos"
 */
export function titleCase(str: string): string {
  if (!str) return str;
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Trunca un string a una longitud máxima
 * @example truncate("Este es un texto largo", 10) => "Este es un..."
 */
export function truncate(str: string, maxLength: number, suffix = '...'): string {
  if (!str || str.length <= maxLength) return str;
  return str.slice(0, maxLength - suffix.length) + suffix;
}

/**
 * Genera iniciales desde un nombre completo
 * @example getInitials("Juan Carlos García") => "JC"
 */
export function getInitials(name: string, maxInitials = 2): string {
  if (!name) return '';
  return name
    .split(' ')
    .filter(word => word.length > 0)
    .slice(0, maxInitials)
    .map(word => word.charAt(0).toUpperCase())
    .join('');
}

// ============================================================================
// CATALOG PARSERS
// ============================================================================

interface CatalogItem {
  value: string;
  label: string;
  category?: string;
  description?: string;
  riskLevel?: string;
}

/**
 * Transforma el catálogo de ocupaciones para usar description como label y code como value
 * @example parseOccupationsCatalog(catalog) => [{ value: "1111", label: "Legisladores" }, ...]
 */
export function parseOccupationsCatalog(items: CatalogItem[]): { value: string; label: string }[] {
  return items.map(item => ({
    value: item.value,
    label: item.description || item.label,
  }));
}

// ============================================================================
// COPY TO CLIPBOARD
// ============================================================================

/**
 * Copia texto al portapapeles
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback para navegadores antiguos
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    return true;
  } catch {
    return false;
  }
}
